import { parseSsoDiscovery, type ISsoDiscovery } from '@d-orca/shared/client/utils';
import { BFF_BASE_URL, credFetch } from '@apis/auth';

/**
 * Enterprise SSO from the browser (spec 2026-10-15). Two calls only:
 *
 *  - `discoverSso` — the email-first step: does this address sign in with its
 *    company account?
 *  - `ssoStartUrl` — where to NAVIGATE (not fetch) to start one. The BFF sets an
 *    httpOnly cookie holding the login's state and PKCE verifier and sends the
 *    browser on to the company sign-in; it comes back to the BFF's callback,
 *    which sets the session cookie and lands on `returnTo` (or on
 *    `/login?error=…`). No token or code is ever visible to this page.
 */

/** Never throws: when the answer is unavailable the page falls back to today's password step. */
export async function discoverSso(email: string): Promise<ISsoDiscovery> {
  try {
    const res = await credFetch('/api/auth/sso/discover', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim() }),
    });
    if (!res.ok) return { sso: false };
    return parseSsoDiscovery(await res.json());
  } catch {
    return { sso: false };
  }
}

export function ssoStartUrl(email: string, returnTo: string): string {
  const query = new URLSearchParams({ email: email.trim(), returnTo });
  return `${BFF_BASE_URL}/api/auth/sso/start?${query.toString()}`;
}

/** Leave the app for the company sign-in. A full navigation: the BFF answers with redirects and cookies. */
export function startSsoLogin(email: string, returnTo: string): void {
  window.location.assign(ssoStartUrl(email, returnTo));
}
